import { useQuery } from '@tanstack/react-query';
import { ChevronRight, Layers, Package } from 'lucide-react';
import type { ReactNode } from 'react';
import { api } from '../lib/api';
import { formatNumber, timezoneOffsetMinutes } from '../lib/format';
import type { AppKey, AppSummary } from '../lib/types';
import { ErrorNote, Skeleton, cn } from './ui';

/** Apps and their live device counts; shared by the Users and Compose flows. */
export function useApps() {
  return useQuery({
    queryKey: ['apps'],
    queryFn: () =>
      api.get<AppSummary[]>('/api/apps', {
        tz_offset: timezoneOffsetMinutes(),
      }),
    staleTime: 60_000,
  });
}

/**
 * Step one of both the Users and Notifications flows: pick an app. Everything
 * downstream is scoped to it, which is also what keeps a broadcast from ever
 * addressing more than one app's push credentials.
 *
 * Browsing can also span every app — pass [onSelectAll] for an "All apps"
 * card, selected while [value] is unset. Compose never does, so a send still
 * always names exactly one app.
 */
export function AppPicker({
  value,
  onSelect,
  onSelectAll,
}: {
  value: AppKey | undefined;
  onSelect: (appKey: AppKey) => void;
  onSelectAll?: () => void;
}) {
  const apps = useApps();

  if (apps.isError) return <ErrorNote error={apps.error} />;

  if (apps.isPending) {
    return (
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {Array.from({ length: 3 }, (_, index) => (
          <Skeleton key={index} className="h-[104px]" />
        ))}
      </div>
    );
  }

  const all = apps.data ?? [];
  const sum = (pick: (app: AppSummary) => number) =>
    all.reduce((total, app) => total + pick(app), 0);

  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
      {onSelectAll && (
        <AppCard
          icon={<Layers size={16} />}
          label="All apps"
          selected={value === undefined}
          onClick={onSelectAll}
          total={sum((app) => app.total)}
          premium={sum((app) => app.premium)}
          newToday={sum((app) => app.new_today)}
        />
      )}
      {all.map((app) => (
        <AppCard
          key={app.app_key}
          icon={<Package size={16} />}
          label={app.app_label}
          selected={app.app_key === value}
          onClick={() => onSelect(app.app_key)}
          total={app.total}
          premium={app.premium}
          newToday={app.new_today}
        />
      ))}
    </div>
  );
}

function AppCard({
  icon,
  label,
  selected,
  onClick,
  total,
  premium,
  newToday,
}: {
  icon: ReactNode;
  label: string;
  selected: boolean;
  onClick: () => void;
  total: number;
  premium: number;
  newToday: number;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        'group rounded-xl border p-4 text-left shadow-sm transition-colors',
        selected
          ? 'border-brand bg-brand-soft'
          : 'border-line bg-surface hover:border-line-strong hover:bg-raised',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div
          className={cn(
            'flex items-center gap-2',
            selected ? 'text-brand' : 'text-ink-3',
          )}
        >
          {icon}
          <span className="font-medium text-ink">{label}</span>
        </div>
        <ChevronRight
          size={16}
          className={cn(
            'shrink-0 transition-transform',
            selected ? 'text-brand' : 'text-ink-3 group-hover:translate-x-0.5',
          )}
        />
      </div>

      <dl className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs">
        <div>
          <dt className="text-ink-3">Devices</dt>
          <dd className="tnum mt-0.5 text-sm font-semibold text-ink">
            {formatNumber(total)}
          </dd>
        </div>
        <div>
          <dt className="text-ink-3">Premium</dt>
          <dd className="tnum mt-0.5 text-sm font-semibold text-ink">
            {formatNumber(premium)}
          </dd>
        </div>
        <div>
          <dt className="text-ink-3">New today</dt>
          <dd className="tnum mt-0.5 text-sm font-semibold text-ink">
            {formatNumber(newToday)}
          </dd>
        </div>
      </dl>
    </button>
  );
}
